import {
  Button,
  ButtonIcon,
  Icon,
  Typography,
  type IconKind,
} from "@saviynt/design-system";
import { useState } from "react";
import { useIntl } from "react-intl";

interface UserProfileProps {
  onLogout: () => void;
  /** When provided, trigger shows avatar + name instead of icon only */
  userName?: string;
  /** When provided with onToggleTheme, shows dark/light mode switcher in dropdown */
  theme?: "light" | "dark";
  onToggleTheme?: () => void;
}

/** Menu option that closes the dropdown on click */
interface MenuItemConfig {
  type: "item";
  iconKind: IconKind;
  messageId: string;
  defaultMessage: string;
  description: string;
  action: "close" | "logout";
  className?: string;
}

/** Menu option with a toggle switch */
interface MenuItemWithToggleConfig {
  type: "toggle";
  iconKind: IconKind;
  messageId: string;
  defaultMessage: string;
  description: string;
  stateKey: "fullScreen" | "emailNotifications";
}

/** Visual separator between sections */
interface MenuDividerConfig {
  type: "divider";
}

type MenuOptionConfig =
  | MenuItemConfig
  | MenuItemWithToggleConfig
  | MenuDividerConfig;

const MENU_OPTIONS: MenuOptionConfig[] = [
  /* 
  {
    type: "item",
    iconKind: "Info" as IconKind,
    messageId: "UserProfile.about",
    defaultMessage: "About",
    description: "About menu item",
    action: "close",
  },
  {
    type: "toggle",
    iconKind: "Expand" as IconKind,
    messageId: "UserProfile.fullScreen",
    defaultMessage: "Full Screen",
    description: "Full screen menu item",
    stateKey: "fullScreen",
  },
  {
    type: "item",
    iconKind: "Lock" as IconKind,
    messageId: "UserProfile.changePassword",
    defaultMessage: "Change Password",
    description: "Change password menu item",
    action: "close",
  },
  {
    type: "item",
    iconKind: "Globe",
    messageId: "UserProfile.language",
    defaultMessage: "Language",
    description: "Language menu item",
    action: "close",
  },
  */
  { type: "divider" },
  {
    type: "item",
    iconKind: "Logout",
    messageId: "UserProfile.logOut",
    defaultMessage: "Log Out",
    description: "Log out menu item",
    action: "logout",
    className:
      "text-[var(--color-foreground-danger-bold,#dc2626)] hover:bg-[rgba(55,81,255,0.08)]",
  },
];

function Divider() {
  return (
    <div
      className="border-t border-[var(--color-border-secondary-decorative,#ebecf2)] my-1"
      role="separator"
      aria-hidden
    />
  );
}

function MenuItem({
  iconKind,
  label,
  onClick,
  className = "",
}: {
  iconKind: IconKind;
  label: string;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <Button
      kind="ghost"
      type="button"
      onClick={onClick}
      className={`flex items-center gap-3 w-full px-3 py-2.5 text-left rounded hover:bg-[rgba(55,81,255,0.08)] transition-colors justify-start ${className}`}
      leftIcon={<Icon kind={iconKind} size="small" />}
    >
      <Typography kind="body2" className="flex-1">
        {label}
      </Typography>
    </Button>
  );
}

function MenuItemWithToggle({
  iconKind,
  label,
  checked,
  onToggle,
}: {
  iconKind: IconKind;
  label: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="flex items-center gap-3 w-full px-3 py-2.5 rounded hover:bg-[rgba(55,81,255,0.08)]">
      <Icon kind={iconKind} size="small" />
      <Typography kind="body2" className="flex-1">
        {label}
      </Typography>
      <Button
        kind="ghost"
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-0 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 p-0 min-w-0 ${
          checked
            ? "bg-[var(--color-background-primary-bold,#3751ff)]"
            : "bg-[var(--color-border-neutral-decorative,#ebecf2)]"
        }`}
        {...({
          role: "switch",
          "aria-checked": checked,
        } as React.ButtonHTMLAttributes<HTMLButtonElement>)}
      >
        <span
          className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition translate-y-0.5 ${
            checked ? "translate-x-4" : "translate-x-0.5"
          }`}
        />
      </Button>
    </div>
  );
}

export function UserProfile({
  onLogout,
  userName,
  theme,
  onToggleTheme,
}: UserProfileProps) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState(true);

  const toggleOpen = () => setOpen((prev) => !prev);
  const closeMenu = () => setOpen(false);

  const handleThemeToggle = () => {
    onToggleTheme?.();
    closeMenu();
  };

  const handleAction = (action: "close" | "logout") => {
    if (action === "logout") {
      closeMenu();
      onLogout();
    } else {
      closeMenu();
    }
  };

  const toggleState = (stateKey: "fullScreen" | "emailNotifications") => {
    if (stateKey === "fullScreen") setFullScreen((prev) => !prev);
    else setEmailNotifications((prev) => !prev);
  };

  const getToggleChecked = (stateKey: "fullScreen" | "emailNotifications") =>
    stateKey === "fullScreen" ? fullScreen : emailNotifications;

  const ariaLabel = intl.formatMessage({
    id: "v4F9z2",
    defaultMessage: "User profile menu",
    description: "User profile menu trigger aria label",
  });

  return (
    <div className="relative">
      {userName ? (
        <Button
          kind="ghost"
          type="button"
          onClick={toggleOpen}
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-haspopup="true"
          className="flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-[var(--color-foreground-neutral-bold)] hover:bg-[rgba(55,81,255,0.08)] transition-colors justify-start"
          leftIcon={
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--color-background-secondary-bold,#254b86)]">
              <Icon kind="User" size="small" />
            </div>
          }
        >
          <Typography kind="body2" className="font-medium">
            {userName}
          </Typography>
        </Button>
      ) : (
        <ButtonIcon
          kind="outlinedNeutral"
          icon={<Icon kind="User" size="large" />}
          size="large"
          ariaLabel={ariaLabel}
          aria-expanded={open}
          aria-haspopup="true"
          onClick={toggleOpen}
        />
      )}
      {open && (
        <>
          <div className="fixed inset-0 z-10" aria-hidden onClick={closeMenu} />
          <div className="absolute right-0 top-full mt-1 z-30 min-w-[220px] py-1 bg-[var(--color-background-neutral-subtlest,#fff)] rounded-lg shadow-lg border border-[var(--color-border-secondary-subtlest,#cfd9e9)]">
            {theme != null && onToggleTheme && (
              <MenuItem
                iconKind={theme === "light" ? "Dark" : "Light"}
                label={
                  theme === "light"
                    ? intl.formatMessage({
                        id: "7WvIGs",
                        defaultMessage: "Dark mode",
                        description: "Theme switcher label",
                      })
                    : intl.formatMessage({
                        id: "6X7/HD",
                        defaultMessage: "Light mode",
                        description: "Theme switcher label",
                      })
                }
                onClick={handleThemeToggle}
              />
            )}
            {MENU_OPTIONS.map((option, index) => {
              if (option.type === "divider") {
                return <Divider key={`divider-${index}`} />;
              }
              if (option.type === "toggle") {
                const label = intl.formatMessage({
                  id: option.messageId,
                  defaultMessage: option.defaultMessage,
                  description: option.description,
                });
                return (
                  <MenuItemWithToggle
                    key={option.stateKey}
                    iconKind={option.iconKind}
                    label={label}
                    checked={getToggleChecked(option.stateKey)}
                    onToggle={() => toggleState(option.stateKey)}
                  />
                );
              }
              const label = intl.formatMessage({
                id: option.messageId,
                defaultMessage: option.defaultMessage,
                description: option.description,
              });
              return (
                <MenuItem
                  key={`${option.messageId}-${index}`}
                  iconKind={option.iconKind}
                  label={label}
                  onClick={() => handleAction(option.action)}
                  className={option.className}
                />
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
